import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { motion } from 'framer-motion';
import { AlertTriangle, Check, RadioTower, BookText, Boxes } from 'lucide-react';
import { useMobile } from './hooks/useMobile';
import { useReducedMotion } from './hooks/useReducedMotion';
import { cn } from '@/lib/utils';

gsap.registerPlugin(ScrollTrigger);

// The vetted trade vocabulary the engine is allowed to use (app/core/logistics/knowledge.py).
const TRADE_TERMS = [
  'B/L', 'demurrage', 'HS code', 'FCL / LCL', 'blank sailing',
  'AEO', 'Bill of Entry', 'detention', 'NVOCC', 'rolled cargo',
];

// The eight logistics verticals from onboarding (LOGISTICS_TYPE_OPTIONS).
const VERTICALS = [
  'Freight forwarding', '3PL', 'Warehousing', 'Last-mile',
  'Customs broking', 'Project cargo', 'Cold chain', 'Fleet',
];

const SUPPORTING = [
  {
    icon: RadioTower,
    title: 'Same-day disruption alerts',
    body: 'A port closure or strike breaks at 9am. A calm "what this means for your cargo" post is drafted by 9:15 — deduped, credibility-checked, and waiting in your Feed. No manual monitoring.',
    render: () => (
      <div className="flex flex-col gap-2">
        <div
          className="inline-flex w-fit items-center gap-2 px-3 py-1.5 font-mono text-xs"
          style={{ background: 'rgba(217,119,87,0.10)', border: '1px solid rgba(217,119,87,0.35)', color: '#B5502E' }}
        >
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: '#B5502E' }} />
          PORT CONGESTION · HIGH · TODAY
        </div>
        <div className="h-px w-full" style={{ background: 'var(--color-hairline)' }} />
        <p className="font-body text-sm leading-snug text-steel-dark">
          "Nhava Sheva is backed up again. Here's how we're keeping your reefer boxes moving this week…"
        </p>
      </div>
    ),
  },
  {
    icon: BookText,
    title: 'Speaks the trade, not model guesswork',
    body: 'Hand-vetted knowledge per vertical — the language, the buyer\'s real fears, what a good story looks like. The model may only use these trade facts, never invent them.',
    render: () => (
      <div className="flex flex-wrap gap-1.5">
        {TRADE_TERMS.map((t) => (
          <span
            key={t}
            className="px-2.5 py-1 font-mono text-xs text-ink"
            style={{ background: '#F0EFEA', border: '1px solid var(--color-hairline)' }}
          >
            {t}
          </span>
        ))}
      </div>
    ),
  },
  {
    icon: Boxes,
    title: 'Eight verticals, one calm B2B voice',
    body: 'Each segment gets its own trade knowledge and an insider tone built on authority and risk-reduction — never consumer hype, FOMO, or emoji.',
    render: () => (
      <div className="grid grid-cols-2 gap-1.5">
        {VERTICALS.map((v) => (
          <span
            key={v}
            className="truncate px-2.5 py-1.5 font-body text-xs text-steel-dark"
            style={{ background: '#FFFFFF', border: '1px solid var(--color-hairline)' }}
          >
            {v}
          </span>
        ))}
      </div>
    ),
  },
];

export function BuiltForLogisticsSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const isMobile = useMobile();
  const prefersReduced = useReducedMotion();

  useEffect(() => {
    if (prefersReduced || !sectionRef.current) return;
    const ctx = gsap.context(() => {
      gsap.from('.bfl-header > *', {
        opacity: 0,
        y: 30,
        stagger: 0.1,
        duration: 0.7,
        ease: 'power3.out',
        scrollTrigger: { trigger: '.bfl-header', start: 'top 85%', toggleActions: 'play none none none' },
      });
    }, sectionRef);
    return () => ctx.revert();
  }, [isMobile, prefersReduced]);

  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden bg-paper"
      style={{ paddingTop: 'clamp(80px, 10vh, 140px)', paddingBottom: 'clamp(80px, 10vh, 140px)' }}
    >
      <div className="relative z-10 mx-auto max-w-6xl px-6 lg:px-8">
        {/* Header */}
        <div className="bfl-header mb-14 max-w-2xl">
          <p
            className="mb-4 font-mono uppercase text-steel-dark"
            style={{ fontSize: 'clamp(0.875rem, 1vw, 1.1rem)', fontWeight: 600, letterSpacing: '0.15em' }}
          >
            Built for Logistics
          </p>
          <h2
            className="mb-4 text-ink font-display"
            style={{ fontSize: 'clamp(2rem, 4vw + 0.5rem, 3.75rem)', lineHeight: 1.1 }}
          >
            Trained on the trade, <span className="text-cargo">not bolted on</span>
          </h2>
          <p
            className="max-w-[58ch] text-steel-dark font-body"
            style={{ fontSize: 'clamp(1.25rem, 1vw + 0.9rem, 1.5rem)', lineHeight: 1.6 }}
          >
            Every other tool treats "logistics" as a dropdown. ZUPA knows freight, customs, and
            the supply chain — so every post reads like it was written by someone who's actually
            filed a Bill of Entry.
          </p>
        </div>

        {/* Featured — the credibility check */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="mb-6 grid gap-8 bg-white p-8 lg:grid-cols-[0.9fr_1.1fr] lg:p-12"
          style={{ border: '1px solid var(--color-hairline)' }}
        >
          {/* Left: the pitch */}
          <div className="flex flex-col justify-center">
            <div
              className="mb-5 inline-flex w-fit items-center gap-2 px-3 py-1.5 font-mono text-xs uppercase"
              style={{ background: 'rgba(217,119,87,0.10)', color: '#B5502E', letterSpacing: '0.1em' }}
            >
              <AlertTriangle className="h-3.5 w-3.5" />
              The credibility check
            </div>
            <h3
              className="mb-4 text-ink font-display"
              style={{ fontSize: 'clamp(1.6rem, 2.5vw, 2.25rem)', lineHeight: 1.15 }}
            >
              One wrong term and a freight buyer stops reading
            </h3>
            <p className="max-w-[42ch] text-steel-dark font-body" style={{ fontSize: '1.2rem', lineHeight: 1.6 }}>
              Before a post is ever approvable, deterministic trade rules — not the AI — catch
              what an outsider would miss: impossible guarantees, wrong Incoterms, consumer hype.
              Each flag comes with the plain-English reason and a safer rewrite.
            </p>
          </div>

          {/* Right: the flagged draft → rewrite */}
          <div className="flex flex-col gap-4">
            {/* Flagged draft */}
            <div className="bg-paper p-5" style={{ border: '1px solid var(--color-hairline)' }}>
              <p className="mb-3 font-mono text-xs uppercase text-steel" style={{ letterSpacing: '0.1em' }}>
                Draft
              </p>
              <p className="font-body text-ink" style={{ fontSize: '1.05rem', lineHeight: 1.6 }}>
                We guarantee{' '}
                <span
                  style={{
                    background: 'rgba(217,119,87,0.16)',
                    textDecoration: 'underline wavy #B5502E',
                    textUnderlineOffset: '3px',
                    padding: '0 2px',
                  }}
                >
                  100% on-time delivery — zero delays, every shipment
                </span>{' '}
                🚀
              </p>
              <div
                className="mt-3 flex items-start gap-2 px-3 py-2 font-body text-sm"
                style={{ background: 'rgba(217,119,87,0.08)', color: '#B5502E' }}
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>Naive claim — no forwarder controls port congestion or customs. Buyers read this as amateur.</span>
              </div>
            </div>

            {/* Rewrite */}
            <div className="bg-white p-5" style={{ border: '1px solid rgba(47,158,68,0.35)' }}>
              <p className="mb-3 flex items-center gap-1.5 font-mono text-xs uppercase" style={{ letterSpacing: '0.1em', color: '#2F9E44' }}>
                <Check className="h-3.5 w-3.5" /> Passes the check
              </p>
              <p className="font-body text-ink" style={{ fontSize: '1.05rem', lineHeight: 1.6 }}>
                When a vessel rolled at Nhava Sheva last month, we had the client's cargo on the
                next sailing within 48 hours. That's what reliability actually looks like.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Supporting three-up */}
        <div className={cn('grid gap-6', isMobile ? 'grid-cols-1' : 'grid-cols-3')}>
          {SUPPORTING.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.6, delay: 0.1 * i, ease: [0.16, 1, 0.3, 1] }}
              className="flex flex-col bg-white p-7"
              style={{ border: '1px solid var(--color-hairline)' }}
            >
              <span
                className="mb-5 inline-flex h-11 w-11 items-center justify-center"
                style={{ background: 'rgba(217,119,87,0.10)', color: '#B5502E' }}
              >
                <f.icon className="h-5 w-5" />
              </span>
              <h3 className="mb-2 text-ink font-display" style={{ fontSize: 'clamp(1.25rem, 1.6vw, 1.5rem)', lineHeight: 1.2 }}>
                {f.title}
              </h3>
              <p className="mb-6 text-steel-dark font-body" style={{ fontSize: '1.05rem', lineHeight: 1.55 }}>
                {f.body}
              </p>
              <div className="mt-auto">{f.render()}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
